import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../../hooks/axios";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";

interface Fare {
  id: string;
  vehicleTypeId: string;
  serialNo: number;
  uptoMiles: number;
  farePrice: number;
}

interface VehicleType {
  model: string;
  name?: string | null;
  description: string;
  remarks: string;
  seatCapacity: number;
  pricePercentage: number;
  status: boolean;
  imageName: string;
  fareList: Fare[];
}

const DetailRow = ({ label, value }: { label: string; value: string | number | boolean }) => (
  <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
    <dt className="text-sm font-medium text-gray-500">{label}</dt>
    <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
      {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value}
    </dd>
  </div>
);

export default function VehicleTypeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [vehicleType, setVehicleType] = useState<VehicleType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchVehicleTypeData = useCallback(async () => {
    try {
      const response = await axiosInstance.get(`/vehicletypes/${id}`);
      setVehicleType(response.data);
    } catch (error) {
      setError("Failed to fetch vehicle type data");
      console.error("Error fetching vehicle type:", error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchVehicleTypeData();
  }, [fetchVehicleTypeData]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="bg-red-50 border border-red-200 text-red-600 rounded-md p-4">
          {error}
        </div>
      </div>
    );
  }

  if (!vehicleType) {
    return (
      <div className="space-y-6">
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-600 rounded-md p-4">
          Vehicle type not found
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center mb-6">
        <PageBreadcrumb pageTitle="Vehicle Type Details" />
        <div className="space-x-4">
          <button
            onClick={() => navigate(`/vehicle-type/edit/${id}`)}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            Edit
          </button>
          <button
            onClick={() => navigate("/vehicle-type")}
            className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            Back to List
          </button>
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-theme-sm dark:border-gray-800 dark:bg-white/[0.03]">
        <div className="border-t border-gray-200">
          <dl>
            <DetailRow label="Name" value={vehicleType.name || vehicleType.description} />
            <DetailRow label="Description" value={vehicleType.description} />
            <DetailRow label="Remarks" value={vehicleType.remarks} />
            <DetailRow label="Seat Capacity" value={vehicleType.seatCapacity} />
            <DetailRow label="Price Percentage" value={`${vehicleType.pricePercentage}%`} />
            <DetailRow label="Status" value={vehicleType.status} />
            <DetailRow label="Image Name" value={vehicleType.imageName} />
          </dl>
        </div>

        {/* Fare List Section */}
        <div className="px-6 py-4 border-t border-gray-200">
          <h3 className="text-lg font-semibold mb-4">Fare List</h3>
          
          {vehicleType.fareList.length === 0 ? (
            <p className="text-gray-500 italic">No fares available.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-800 dark:divide-gray-800">
                <thead className="bg-gray-50 dark:bg-white/[0.02]">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Serial No
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Up to Miles
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Rate per Mile (£)
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-800 dark:bg-transparent">
                  {vehicleType.fareList.map((fare: Fare) => (
                    <tr key={fare.id}>
                      <td className="px-4 py-2 whitespace-nowrap">
                        {fare.serialNo}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        {fare.uptoMiles != null ? `${fare.uptoMiles} mi` : "—"}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        {fare.farePrice != null ? `£${fare.farePrice}/mi` : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {vehicleType.imageName && (
          <div className="px-6 py-4 border-t border-gray-200">
            <h3 className="text-lg font-medium mb-4">Vehicle Image</h3>
            <div className="relative w-48 h-48 border-2 border-gray-200 rounded-lg overflow-hidden bg-gray-50">
              <img
                src={`/images/vehicle/${vehicleType.imageName}`}
                alt="Vehicle"
                className="absolute inset-0 w-full h-full object-contain"
                onError={(e) => {
                  if (!e.currentTarget.src.includes('placeholder-image.png')) {
                    console.error(`Error loading image: ${e.currentTarget.src}`);
                    e.currentTarget.src = '/images/placeholder-image.png';
                    console.error('Switched to placeholder image');
                  }
                }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
} 